import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Alert,
  Box,
  Checkbox,
  DialogContentText,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Snackbar,
  TextField,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import CloudSyncOutlinedIcon from "@mui/icons-material/CloudSyncOutlined";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import SearchIcon from "@mui/icons-material/Search";
import SyncIcon from "@mui/icons-material/Sync";

import AppButton from "../ui/AppButton";
import AppSelect from "../ui/AppSelect";
import PageHeader from "../ui/PageHeader";
import DeleteDialog from "../modals/DeleteDialog";
import EmployeeDialog from "../components/employees/EmployeeDialog";
import EmployeeDeptSyncDialog from "../components/employees/EmployeeDeptSyncDialog";
import AzureSyncDialog from "../components/employees/AzureSyncDialog";
import EmployeeTable from "../components/employees/EmployeeTable";

import {
  createEmployee,
  deleteEmployee,
  getEmployees,
  updateEmployee,
  syncEmployeesFromAzure,
} from "../services/employeeService";
import { useUserRole } from "../context/UserRoleContext";
import { USER_ROLES, hasOwnDataScope, hasTeamDataScope } from "../auth/roles";
import { useDepartments } from "../context/DepartmentsContext";

function Employees() {
  const { role, employee, loading: roleLoading } = useUserRole();
  const { departments } = useDepartments();

  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState(null);
  const [managerFilter, setManagerFilter] = useState(null);
  const [roleFilter, setRoleFilter] = useState(null);
  const [loading, setLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [deleteRelated, setDeleteRelated] = useState(false);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [isSyncingAzure, setIsSyncingAzure] = useState(false);
  const [azureSyncOpen, setAzureSyncOpen] = useState(false);
  const [syncDeptsOpen, setSyncDeptsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const departmentOptions = useMemo(
    () => [...departments].sort((a, b) => a.department_name.localeCompare(b.department_name))
      .map((d) => ({ value: d.department_id, label: d.department_name })),
    [departments]
  );

  const managerOptions = useMemo(() => {
    const managerIds = new Set(employees.map((e) => e.manager_id).filter(Boolean));
    return employees
      .filter((e) => managerIds.has(e.employee_id))
      .sort((a, b) => a.employee_name.localeCompare(b.employee_name))
      .map((e) => ({ value: e.employee_id, label: e.employee_name }));
  }, [employees]);

  const roleOptions = useMemo(() => {
    const seen = new Set();
    return employees
      .map((e) => e.role_name)
      .filter((r) => r && !seen.has(r) && seen.add(r))
      .sort()
      .map((r) => ({ value: r, label: r.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) }));
  }, [employees]);

  const filteredEmployees = useMemo(() => {
    const q = search.trim().toLowerCase();
    return employees.filter((emp) => {
      if (q && !emp.employee_name?.toLowerCase().includes(q) &&
          !emp.email?.toLowerCase().includes(q) &&
          !emp.department_name?.toLowerCase().includes(q)) return false;
      if (departmentFilter && emp.department_id !== departmentFilter) return false;
      if (managerFilter && emp.manager_id !== managerFilter) return false;
      if (roleFilter && emp.role_name !== roleFilter) return false;
      return true;
    });
  }, [employees, search, departmentFilter, managerFilter, roleFilter]);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const loadEmployees = useCallback(async () => {
    try {
      setLoading(true);

      // "Own data" roles only ever see their own employee record.
      if (hasOwnDataScope(role, "employees")) {
        setEmployees(employee ? [employee] : []);
        return;
      }

      const managerId = hasTeamDataScope(role, "employees") ? (employee?.employee_id ?? -1) : null;
      const data = await getEmployees("", managerId);
      setEmployees(data);

      // Keep only still-valid selected IDs after reload/filter changes.
      setSelectedEmployeeIds((prev) => {
        const validIds = new Set(data.map((employee) => employee.employee_id));
        return prev.filter((id) => validIds.has(id));
      });
    } catch (error) {
      console.error("Employee Error:", error);
      setSnackbar({
        open: true,
        message: "Failed to load employees",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [role, employee]);

  useEffect(() => {
    if (roleLoading) {
      return;
    }

    loadEmployees();
  }, [loadEmployees, roleLoading]);

  const handleSyncAzure = async () => {
    if (isSyncingAzure) {
      return;
    }

    try {
      setIsSyncingAzure(true);

      await syncEmployeesFromAzure(employee?.employee_id ?? null);

      setSnackbar({
        open: true,
        message: "Azure AD sync started in the background. We'll notify you once it's done.",
        severity: "success",
      });
    } catch (error) {
      console.error("Azure Sync Error:", error);
      const message = error?.response?.data?.detail || "Failed to start Azure AD sync";

      setSnackbar({
        open: true,
        message,
        severity: "error",
      });
    } finally {
      setIsSyncingAzure(false);
    }
  };

  const handleSave = async (data) => {
    if (isSaving) {
      return;
    }

    try {
      setIsSaving(true);

      const payload = {
        ...data,
        department_id: Number(data.department_id),
        manager_id: data.manager_id ? Number(data.manager_id) : null,
        role_name: data.role_name || "member",
      };

      if (selectedEmployee) {
        await updateEmployee(selectedEmployee.employee_id, payload);
      } else {
        await createEmployee(payload);
      }

      setDialogOpen(false);
      setSelectedEmployee(null);

      await loadEmployees();

      setSnackbar({
        open: true,
        message: "Employee saved successfully",
        severity: "success",
      });
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: "Operation failed",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSyncAllDepartments = () => setSyncDeptsOpen(true);

  const handleSyncDepartment = async (emp) => {
    const match = departments.find(
      (d) => d.department_name.toLowerCase() === (emp.designation || "").trim().toLowerCase()
    );
    if (!match) {
      setSnackbar({ open: true, message: `No department found for "${emp.designation || "(no designation)"}".`, severity: "warning" });
      return;
    }
    if (match.department_id === emp.department_id) {
      setSnackbar({ open: true, message: `Department already set to "${match.department_name}".`, severity: "info" });
      return;
    }
    try {
      await updateEmployee(emp.employee_id, {
        employee_name: emp.employee_name,
        email: emp.email,
        designation: emp.designation || null,
        department_id: match.department_id,
        role_name: emp.role_name || "member",
        manager_id: emp.manager_id || null,
      });
      await loadEmployees();
      setSnackbar({ open: true, message: `Department synced to "${match.department_name}".`, severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: error?.response?.data?.detail || "Sync failed", severity: "error" });
    }
  };

  const closeDeleteDialog = () => {
    setDeleteOpen(false);
    setSelectedEmployee(null);
    setDeleteRelated(false);
  };

  const handleDelete = async () => {
    if (!selectedEmployee || isDeleting) {
      return;
    }

    try {
      setIsDeleting(true);

      await deleteEmployee(selectedEmployee.employee_id, deleteRelated);
      closeDeleteDialog();

      await loadEmployees();

      setSnackbar({
        open: true,
        message: deleteRelated
          ? "Employee and related records deleted successfully"
          : "Employee deleted successfully",
        severity: "success",
      });
    } catch (error) {
      closeDeleteDialog();
      console.error(error);
      const message = error?.response?.data?.detail || "Delete failed";
      setSnackbar({
        open: true,
        message,
        severity: "error",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedEmployeeIds.length === 0 || isBulkDeleting) {
      return;
    }

    try {
      setIsBulkDeleting(true);

      let successCount = 0;
      let failedCount = 0;

      for (const employeeId of selectedEmployeeIds) {
        try {
          await deleteEmployee(employeeId, true);
          successCount += 1;
        } catch (error) {
          failedCount += 1;
        }
      }

      setBulkDeleteOpen(false);

      await loadEmployees();

      setSelectedEmployeeIds([]);

      if (failedCount === 0) {
        setSnackbar({
          open: true,
          message: `${successCount} employee(s) deleted successfully`,
          severity: "success",
        });
      } else {
        setSnackbar({
          open: true,
          message: `${successCount} deleted, ${failedCount} failed`,
          severity: "warning",
        });
      }
    } catch (error) {
      console.error(error);
      setBulkDeleteOpen(false);
      setSnackbar({
        open: true,
        message: "Bulk delete failed",
        severity: "error",
      });
    } finally {
      setIsBulkDeleting(false);
    }
  };

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        minHeight: "100vh",
        p: 3,
      }}
    >
      <PageHeader
        title="Employees"
        subtitle="Manage and view all employees in the organization."
        actions={(
          <>
            {role === USER_ROLES.SUPER_ADMIN && (
              <AppButton
                variant="outlined"
                startIcon={<CloudSyncOutlinedIcon />}
                onClick={() => setAzureSyncOpen(true)}
              >
                Import from Azure AD
              </AppButton>
            )}

            {role === USER_ROLES.SUPER_ADMIN && (
              <AppButton
                variant="outlined"
                startIcon={<SyncIcon />}
                onClick={handleSyncAllDepartments}
              >
                Sync Departments
              </AppButton>
            )}

            <AppButton
              variant="outlined"
              color="error"
              startIcon={<DeleteSweepIcon />}
              loading={isBulkDeleting}
              onClick={() => setBulkDeleteOpen(true)}
              disabled={selectedEmployeeIds.length === 0}
            >
              {isBulkDeleting ? "Deleting..." : `Delete All (${selectedEmployeeIds.length})`}
            </AppButton>

            <AppButton
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setSelectedEmployee(null);
                setDialogOpen(true);
              }}
            >
              Add Employee
            </AppButton>
          </>
        )}
      />

      <Box
        sx={{
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          p: 1.5,
          mb: 2,
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search employee name, email or department..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 20, color: "text.secondary" }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            "& .MuiInputBase-root": {
              height: 38,
            },
          }}
        />

        <Box sx={{ display: "flex", gap: 1.5, mt: 1.5, flexWrap: "wrap" }}>
          <Box sx={{ minWidth: 200 }}>
            <AppSelect
              placeholder="All departments"
              isClearable
              options={departmentOptions}
              value={departmentFilter}
              onChange={(val) => setDepartmentFilter(val || null)}
            />
          </Box>
          <Box sx={{ minWidth: 200 }}>
            <AppSelect
              placeholder="All managers"
              isClearable
              options={managerOptions}
              value={managerFilter}
              onChange={(val) => setManagerFilter(val || null)}
            />
          </Box>
          <Box sx={{ minWidth: 180 }}>
            <AppSelect
              placeholder="All roles"
              isClearable
              options={roleOptions}
              value={roleFilter}
              onChange={(val) => setRoleFilter(val || null)}
            />
          </Box>
        </Box>
      </Box>

      <EmployeeTable
        employees={filteredEmployees}
        loading={loading}
        selectedIds={selectedEmployeeIds}
        onSelectionChange={setSelectedEmployeeIds}
        onEdit={(employee) => {
          setSelectedEmployee(employee);
          setDialogOpen(true);
        }}
        onDelete={(employee) => {
          setSelectedEmployee(employee);
          setDeleteRelated(false);
          setDeleteOpen(true);
        }}
        onSync={handleSyncDepartment}
      />

      <AzureSyncDialog
        open={azureSyncOpen}
        onClose={() => setAzureSyncOpen(false)}
        onDone={() => loadEmployees()}
      />

      <EmployeeDeptSyncDialog
        open={syncDeptsOpen}
        onClose={() => setSyncDeptsOpen(false)}
        employees={employees}
        departments={departments}
        onDone={() => loadEmployees()}
      />

      <EmployeeDialog
        open={dialogOpen}
        employee={selectedEmployee}
        isSaving={isSaving}
        onClose={() => {
          if (isSaving) {
            return;
          }
          setDialogOpen(false);
          setSelectedEmployee(null);
        }}
        onSave={handleSave}
      />

      <DeleteDialog
        open={deleteOpen}
        isDeleting={isDeleting}
        title="Delete Employee"
        message="Are you sure you want to delete this employee?"
        itemName={selectedEmployee?.employee_name}
        extra={(
          <FormControlLabel
            sx={{ mt: 1 }}
            control={(
              <Checkbox
                checked={deleteRelated}
                onChange={(event) => setDeleteRelated(event.target.checked)}
                disabled={isDeleting}
              />
            )}
            label="Delete all related records"
          />
        )}
        onClose={() => {
          if (isDeleting) {
            return;
          }
          closeDeleteDialog();
        }}
        onConfirm={handleDelete}
      />

      <DeleteDialog
        open={bulkDeleteOpen}
        onClose={() => {
          if (isBulkDeleting) {
            return;
          }
          setBulkDeleteOpen(false);
        }}
        onConfirm={handleBulkDelete}
        isDeleting={isBulkDeleting}
        title="Delete Selected Employees"
        message="Are you sure you want to delete all selected employees?"
        itemName={`Selected: ${selectedEmployeeIds.length}`}
        extra={(
          <DialogContentText sx={{ mt: 1 }}>
            This will remove related records for each selected employee.
          </DialogContentText>
        )}
        confirmLabel="Delete All"
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default Employees;
